import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarClock,
  Edit,
  Filter,
  ImagePlus,
  ImageOff,
  LogOut,
  Palette,
  RotateCcw,
  Send,
  PlusSquare,
  RefreshCcw,
  Settings,
  Trash2,
  User,
  FileText,
  Plus,
  Download,
  CheckCircle,
  Clock,
  X,
} from 'lucide-react';
import FooterNew from '../components/FooterNew';
import companyLogo from '../assets/images/cc.png';
import { clearAdminSession, getAdminToken, getBackendUrl } from '../utils/auth';
const initialForm = {
  platform: 'Instagram',
  caption: '',
  hashtags: '',
  scheduledAt: '',
};

const initialFilters = {
  platform: 'all',
  status: 'all',
  search: '',
  from: '',
  to: '',
};

const AdminSocialPost = () => {
  const backendUrl = useMemo(() => getBackendUrl(), []);
  const [formData, setFormData] = useState(initialForm);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    totalPages: 1,
    totalCount: 0,
  });
  const [activePanel, setActivePanel] = useState('posts');
  const [showCreatePost, setShowCreatePost] = useState(true);
  const [editingPostId, setEditingPostId] = useState('');
  const [uiTheme, setUiTheme] = useState('ocean');
  const [adminProfile, setAdminProfile] = useState({
    name: 'Admin User',
    email: 'admin@clientcodeai.in',
    role: 'Super Admin',
  });
  const [settingsState, setSettingsState] = useState({
    autoRefresh: false,
    emailAlerts: true,
    compactView: false,
  });

  const initialInvoiceForm = {
    clientName: '',
    clientEmail: '',
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    items: [{ description: '', quantity: 1, rate: 0 }],
    tax: 0,
    notes: '',
  };
  const [invoices, setInvoices] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState(initialInvoiceForm);
  const [invoiceView, setInvoiceView] = useState('list'); // 'list' | 'create' | 'preview'
  const [previewInvoice, setPreviewInvoice] = useState(null);

  const adminToken = getAdminToken();

  const themeMap = {
    ocean: {
      panelBackground: 'linear-gradient(145deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.75))',
      border: '1px solid rgba(99, 102, 241, 0.2)',
      chip: 'rgba(99, 102, 241, 0.12)',
    },
    emerald: {
      panelBackground: 'linear-gradient(145deg, rgba(6, 39, 36, 0.92), rgba(15, 87, 78, 0.72))',
      border: '1px solid rgba(16, 185, 129, 0.28)',
      chip: 'rgba(16, 185, 129, 0.18)',
    },
    sunset: {
      panelBackground: 'linear-gradient(145deg, rgba(53, 24, 13, 0.92), rgba(120, 53, 15, 0.72))',
      border: '1px solid rgba(249, 115, 22, 0.28)',
      chip: 'rgba(249, 115, 22, 0.2)',
    },
  };

  const activeTheme = themeMap[uiTheme] || themeMap.ocean;

  const getMediaSrc = (mediaUrl = '') => {
    if (!mediaUrl) return '';
    if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) return mediaUrl;
    return `${backendUrl}${mediaUrl}`;
  };

  const savePreferences = async (payload) => {
    const response = await fetch(`${backendUrl}/api/admin/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Unable to save preferences');
    }

    if (data?.data?.profile) setAdminProfile(data.data.profile);
    if (data?.data?.settings) setSettingsState(data.data.settings);
    if (data?.data?.theme) setUiTheme(data.data.theme);

    return data;
  };

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/preferences`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Unable to fetch preferences');
      }

      if (data?.data?.profile) setAdminProfile(data.data.profile);
      if (data?.data?.settings) setSettingsState(data.data.settings);
      if (data?.data?.theme) setUiTheme(data.data.theme);
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }
      // Preferences are non-critical — don't block the UI with a hard error
    }
  };

  const fetchPosts = async (nextPage = pagination.page, nextLimit = pagination.limit) => {
    const safePage = Math.max(1, Number.parseInt(String(nextPage), 10) || 1);
    const safeLimit = Math.max(1, Number.parseInt(String(nextLimit), 10) || pagination.limit);

    setListLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.set('page', String(safePage));
      params.set('limit', String(safeLimit));
      if (filters.platform && filters.platform !== 'all') params.set('platform', filters.platform);
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.search.trim()) params.set('search', filters.search.trim());
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);

      const response = await fetch(`${backendUrl}/api/admin/social-posts?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to fetch posts');
      }

      setPosts(Array.isArray(payload.data) ? payload.data : []);
      setPagination({
        page: payload?.pagination?.page || safePage,
        limit: payload?.pagination?.limit || safeLimit,
        totalPages: payload?.pagination?.totalPages || 1,
        totalCount: payload?.totalCount || 0,
      });
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }

      const msg = requestError.message === 'Failed to fetch'
        ? 'Server is waking up, please wait a moment and try again.'
        : requestError.message;
      setError(msg);
    } finally {
      setListLoading(false);
    }
  };

  const resetPostForm = () => {
    setFormData(initialForm);
    setSelectedImage(null);
    setEditingPostId('');
  };

  useEffect(() => {
    if (!adminToken) {
      return;
    }

    try {
      const tokenPayload = JSON.parse(atob(adminToken.split('.')[1] || ''));
      if (tokenPayload?.email) {
        setAdminProfile((prev) => ({ ...prev, email: tokenPayload.email }));
      }
    } catch (_error) {
      // Keep fallback admin profile values if token payload parsing fails.
    }

    fetchPreferences();
    fetchPosts(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  useEffect(() => {
    if (!settingsState.autoRefresh || !adminToken) {
      return undefined;
    }

    const timer = setInterval(() => {
      fetchPosts(pagination.page, pagination.limit);
    }, 20000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsState.autoRefresh, adminToken, filters, pagination.page, pagination.limit]);

  if (!adminToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const payloadData = new FormData();
      payloadData.append('platform', formData.platform);
      payloadData.append('caption', formData.caption);
      payloadData.append('hashtags', formData.hashtags);
      payloadData.append('scheduledAt', formData.scheduledAt);
      if (selectedImage) {
        payloadData.append('image', selectedImage);
      }

      const method = editingPostId ? 'PUT' : 'POST';
      const endpoint = editingPostId
        ? `${backendUrl}/api/admin/social-posts/${editingPostId}`
        : `${backendUrl}/api/admin/social-posts`;

      const response = await fetch(endpoint, {
        method,
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        body: payloadData,
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to save social post');
      }

      setMessage(payload.message || (editingPostId ? 'Social media post updated successfully' : 'Social media post saved successfully'));
      resetPostForm();
      await fetchPosts(1, pagination.limit);
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }

      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPost = (post) => {
    setShowCreatePost(true);
    setActivePanel('posts');
    setEditingPostId(post._id);
    setFormData({
      platform: post.platform || 'Instagram',
      caption: post.caption || '',
      hashtags: Array.isArray(post.hashtags) ? post.hashtags.join(', ') : '',
      scheduledAt: post.scheduledAt ? new Date(post.scheduledAt).toISOString().slice(0, 16) : '',
    });
    setSelectedImage(null);
    setMessage('Editing selected post. Update fields and click update.');
    setError('');
  };

  const handleDeletePost = async (postId) => {
    const isConfirmed = window.confirm('Are you sure you want to delete this post?');
    if (!isConfirmed) return;

    setError('');
    setMessage('');

    try {
      const response = await fetch(`${backendUrl}/api/admin/social-posts/${postId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to delete post');
      }

      if (editingPostId === postId) {
        resetPostForm();
      }

      setMessage(payload.message || 'Post deleted successfully.');
      await fetchPosts(pagination.page, pagination.limit);
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }
      setError(requestError.message);
    }
  };

  const handleRestorePost = async (postId) => {
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${backendUrl}/api/admin/social-posts/${postId}/restore`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to restore post');
      }

      setMessage(payload.message || 'Post restored successfully.');
      await fetchPosts(pagination.page, pagination.limit);
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }
      setError(requestError.message);
    }
  };

  const handleAdminLogout = () => {
    clearAdminSession();
    window.location.href = '/admin/login';
  };

  return (
    <main className="bg-primary min-h-screen pt-28">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-8"
          style={{
            background: activeTheme.panelBackground,
            border: activeTheme.border,
          }}
        >
          <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
            <div>
              <h1 className="text-3xl font-bold text-white">Admin Social Dashboard</h1>
              <p className="text-slate-400 mt-2">Sidebar panel se Post, Admin Profile, Settings aur UI Theme control karein.</p>
            </div>
            <button type="button" className="neon-btn neon-btn-primary" onClick={handleAdminLogout}>
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>

          {error ? <p className="text-rose-300 text-sm mb-4">{error}</p> : null}
          {message ? <p className="text-emerald-300 text-sm mb-4">{message}</p> : null}

          <div className="grid lg:grid-cols-[250px_1fr] gap-6">
            <aside className="rounded-2xl p-4" style={{ border: activeTheme.border, background: 'rgba(2, 6, 23, 0.45)' }}>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setActivePanel('posts');
                    setShowCreatePost(true);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${activePanel === 'posts' ? 'text-white' : 'text-slate-300 hover:text-white'}`}
                  style={activePanel === 'posts' ? { background: activeTheme.chip } : {}}
                >
                  <PlusSquare className="w-4 h-4 inline mr-2" />
                  Post Panel
                </button>
                <button
                  type="button"
                  onClick={() => setActivePanel('profile')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${activePanel === 'profile' ? 'text-white' : 'text-slate-300 hover:text-white'}`}
                  style={activePanel === 'profile' ? { background: activeTheme.chip } : {}}
                >
                  <User className="w-4 h-4 inline mr-2" />
                  Admin Profile
                </button>
                <button
                  type="button"
                  onClick={() => setActivePanel('settings')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${activePanel === 'settings' ? 'text-white' : 'text-slate-300 hover:text-white'}`}
                  style={activePanel === 'settings' ? { background: activeTheme.chip } : {}}
                >
                  <Settings className="w-4 h-4 inline mr-2" />
                  Settings
                </button>
                <button
                  type="button"
                  onClick={() => setActivePanel('theme')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${activePanel === 'theme' ? 'text-white' : 'text-slate-300 hover:text-white'}`}
                  style={activePanel === 'theme' ? { background: activeTheme.chip } : {}}
                >
                  <Palette className="w-4 h-4 inline mr-2" />
                  UI Theme
                </button>
                <button
                  type="button"
                  onClick={() => setActivePanel('salary-slip')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${activePanel === 'salary-slip' ? 'text-white' : 'text-slate-300 hover:text-white'}`}
                  style={activePanel === 'salary-slip' ? { background: activeTheme.chip } : {}}
                >
                  <CalendarClock className="w-4 h-4 inline mr-2" />
                  Salary Slip Generator
                </button>
                <button
                  type="button"
                  onClick={() => { setActivePanel('invoice'); setInvoiceView('list'); }}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${activePanel === 'invoice' ? 'text-white' : 'text-slate-300 hover:text-white'}`}
                  style={activePanel === 'invoice' ? { background: activeTheme.chip } : {}}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Invoice Manager
                </button>
              </div>
            </aside>

            <div className="rounded-2xl p-5" style={{ border: activeTheme.border, background: 'rgba(2, 6, 23, 0.45)' }}>
              {activePanel === 'posts' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-xl font-semibold text-white">Post Management</h2>
                    <div className="flex gap-2">
                      <button type="button" className="neon-btn neon-btn-primary" onClick={() => setShowCreatePost(true)}>
                        <PlusSquare className="w-4 h-4" />
                        Create Post
                      </button>
                      <button type="button" className="neon-btn neon-btn-outline" onClick={() => setShowCreatePost(false)}>
                        <Filter className="w-4 h-4" />
                        View All Posts
                      </button>
                    </div>
                  </div>

                  {showCreatePost ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <select
                          value={formData.platform}
                          onChange={(event) => setFormData((prev) => ({ ...prev, platform: event.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-slate-200"
                        >
                          <option>Instagram</option>
                          <option>LinkedIn</option>
                          <option>Facebook</option>
                          <option>Twitter/X</option>
                          <option>YouTube</option>
                        </select>

                        <input
                          type="datetime-local"
                          required
                          value={formData.scheduledAt}
                          onChange={(event) => setFormData((prev) => ({ ...prev, scheduledAt: event.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-slate-200"
                        />
                      </div>

                      <textarea
                        rows={5}
                        required
                        value={formData.caption}
                        onChange={(event) => setFormData((prev) => ({ ...prev, caption: event.target.value }))}
                        placeholder="Post caption"
                        className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                      />

                      <input
                        type="text"
                        value={formData.hashtags}
                        onChange={(event) => setFormData((prev) => ({ ...prev, hashtags: event.target.value }))}
                        placeholder="#ai, #automation, #clientcode"
                        className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                      />

                      <label className="block rounded-xl border border-accent/20 bg-surface/50 px-4 py-3 text-slate-300 cursor-pointer">
                        <span className="inline-flex items-center gap-2 text-sm font-medium">
                          <ImagePlus className="w-4 h-4" />
                          Choose Image From Gallery
                        </span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                          onChange={(event) => setSelectedImage(event.target.files?.[0] || null)}
                        />
                        <p className="text-xs text-slate-500 mt-1">{selectedImage ? selectedImage.name : 'No image selected'}</p>
                      </label>

                      <button type="submit" disabled={loading} className="neon-btn neon-btn-primary w-full justify-center">
                        {loading ? <CalendarClock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                        {loading ? 'Saving...' : editingPostId ? 'Update Social Post' : 'Save Social Post'}
                      </button>
                      {editingPostId ? (
                        <button
                          type="button"
                          className="neon-btn neon-btn-outline w-full justify-center"
                          onClick={resetPostForm}
                        >
                          Cancel Edit
                        </button>
                      ) : null}
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">All Posts With Filter</h3>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <select
                          value={filters.platform}
                          onChange={(event) => setFilters((prev) => ({ ...prev, platform: event.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-slate-200"
                        >
                          <option value="all">All Platforms</option>
                          <option value="Instagram">Instagram</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="Facebook">Facebook</option>
                          <option value="Twitter/X">Twitter/X</option>
                          <option value="YouTube">YouTube</option>
                        </select>
                        <select
                          value={filters.status}
                          onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-slate-200"
                        >
                          <option value="all">All Active</option>
                          <option value="upcoming">Upcoming</option>
                          <option value="published">Published</option>
                          <option value="trashed">Trashed</option>
                        </select>
                        <input
                          type="date"
                          value={filters.from}
                          onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-slate-200"
                        />
                        <input
                          type="date"
                          value={filters.to}
                          onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-slate-200"
                        />
                      </div>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={filters.search}
                          onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                          placeholder="Search caption/hashtags"
                          className="flex-1 px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                        />
                        <button type="button" className="neon-btn neon-btn-outline" onClick={() => fetchPosts(1, pagination.limit)}>
                          <Filter className="w-4 h-4" />
                          Apply
                        </button>
                        <button
                          type="button"
                          className="neon-btn neon-btn-primary"
                          onClick={() => {
                            setFilters(initialFilters);
                            setTimeout(() => fetchPosts(1, pagination.limit), 0);
                          }}
                        >
                          <RefreshCcw className="w-4 h-4" />
                          Reset
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
                        <p>Total Posts: <span className="text-white">{pagination.totalCount}</span></p>
                        <div className="flex items-center gap-2">
                          <span>Per page</span>
                          <select
                            value={pagination.limit}
                            onChange={(event) => {
                              const nextLimit = Number(event.target.value);
                              setPagination((prev) => ({ ...prev, limit: nextLimit, page: 1 }));
                              fetchPosts(1, nextLimit);
                            }}
                            className="px-2 py-1 rounded-lg bg-surface/60 border border-accent/20 text-slate-200"
                          >
                            <option value={8}>8</option>
                            <option value={12}>12</option>
                            <option value={16}>16</option>
                          </select>
                        </div>
                      </div>

                      <div className={`grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[620px] overflow-auto pr-1 ${settingsState.compactView ? 'text-xs' : ''}`}>
                        {listLoading ? <p className="text-slate-400 text-sm">Loading posts...</p> : null}
                        {!listLoading && posts.length === 0 ? <p className="text-slate-400 text-sm">No posts found.</p> : null}
                        {posts.map((post) => (
                          <article
                            key={post._id}
                            className="rounded-2xl border border-accent/20 bg-[linear-gradient(180deg,rgba(15,23,42,0.68),rgba(2,6,23,0.7))] p-4 min-h-[380px] flex flex-col shadow-[0_8px_24px_rgba(2,6,23,0.35)]"
                          >
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <span className="text-xs px-2.5 py-1 rounded-full bg-accent/10 text-ai-glow border border-accent/20">
                                {post.platform}
                              </span>
                              <span className="text-[11px] text-slate-400">
                                {new Date(post.scheduledAt).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-slate-100 mb-2 leading-relaxed">{post.caption}</p>
                            {Array.isArray(post.hashtags) && post.hashtags.length ? (
                              <p className="text-xs text-slate-400 mb-3">{post.hashtags.join(', ')}</p>
                            ) : null}
                            <div className="flex flex-wrap gap-1 mb-2">
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-300">
                                Updated by: {post.updatedBy || post.createdBy || 'admin'}
                              </span>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-300">
                                Updated: {new Date(post.updatedAt).toLocaleString()}
                              </span>
                            </div>
                            {post.mediaUrl ? (
                              <img
                                src={getMediaSrc(post.mediaUrl)}
                                alt="post media"
                                className="w-full h-44 object-cover rounded-xl border border-accent/10"
                              />
                            ) : (
                              <div className="w-full h-44 rounded-xl border border-dashed border-accent/25 bg-slate-900/40 flex flex-col items-center justify-center text-slate-400 gap-2">
                                <ImageOff className="w-5 h-5" />
                                <p className="text-xs">No image attached</p>
                              </div>
                            )}
                            <div className="flex items-center justify-end gap-2 mt-auto pt-4">
                              {post.isDeleted ? (
                                <button
                                  type="button"
                                  className="neon-btn neon-btn-outline"
                                  onClick={() => handleRestorePost(post._id)}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                  Restore
                                </button>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="neon-btn neon-btn-outline"
                                    onClick={() => handleEditPost(post)}
                                  >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="neon-btn neon-btn-primary"
                                    onClick={() => handleDeletePost(post._id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Trash
                                  </button>
                                </>
                              )}
                            </div>
                          </article>
                        ))}
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <button
                          type="button"
                          className="neon-btn neon-btn-outline"
                          disabled={pagination.page <= 1}
                          onClick={() => fetchPosts(pagination.page - 1, pagination.limit)}
                        >
                          Prev
                        </button>
                        <p className="text-sm text-slate-300">
                          Page {pagination.page} of {pagination.totalPages}
                        </p>
                        <button
                          type="button"
                          className="neon-btn neon-btn-outline"
                          disabled={pagination.page >= pagination.totalPages}
                          onClick={() => fetchPosts(pagination.page + 1, pagination.limit)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {activePanel === 'profile' ? (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-white">Admin Profile</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={adminProfile.name}
                      onChange={(event) => setAdminProfile((prev) => ({ ...prev, name: event.target.value }))}
                      placeholder="Admin Name"
                      className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                    />
                    <input
                      type="text"
                      value={adminProfile.role}
                      onChange={(event) => setAdminProfile((prev) => ({ ...prev, role: event.target.value }))}
                      placeholder="Role"
                      className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                    />
                  </div>
                  <input
                    type="email"
                    value={adminProfile.email}
                    onChange={(event) => setAdminProfile((prev) => ({ ...prev, email: event.target.value }))}
                    placeholder="Admin Email"
                    className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                  />
                  <button
                    type="button"
                    className="neon-btn neon-btn-primary"
                    onClick={async () => {
                      setError('');
                      try {
                        await savePreferences({
                          profile: { name: adminProfile.name, role: adminProfile.role },
                        });
                        setMessage('Admin profile updated successfully.');
                      } catch (requestError) {
                        if (requestError.message.toLowerCase().includes('token')) {
                          clearAdminSession();
                        }
                        setError(requestError.message);
                      }
                    }}
                  >
                    Save Profile
                  </button>
                </div>
              ) : null}

              {activePanel === 'settings' ? (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-white">Settings</h2>
                  <label className="flex items-center justify-between rounded-xl bg-surface/50 border border-accent/20 px-4 py-3 text-slate-200">
                    Auto Refresh Post List
                    <input
                      type="checkbox"
                      checked={settingsState.autoRefresh}
                      onChange={(event) => setSettingsState((prev) => ({ ...prev, autoRefresh: event.target.checked }))}
                    />
                  </label>
                  <label className="flex items-center justify-between rounded-xl bg-surface/50 border border-accent/20 px-4 py-3 text-slate-200">
                    Email Alerts
                    <input
                      type="checkbox"
                      checked={settingsState.emailAlerts}
                      onChange={(event) => setSettingsState((prev) => ({ ...prev, emailAlerts: event.target.checked }))}
                    />
                  </label>
                  <label className="flex items-center justify-between rounded-xl bg-surface/50 border border-accent/20 px-4 py-3 text-slate-200">
                    Compact View
                    <input
                      type="checkbox"
                      checked={settingsState.compactView}
                      onChange={(event) => setSettingsState((prev) => ({ ...prev, compactView: event.target.checked }))}
                    />
                  </label>
                  <button
                    type="button"
                    className="neon-btn neon-btn-primary"
                    onClick={async () => {
                      setError('');
                      try {
                        await savePreferences({ settings: settingsState });
                        setMessage('Settings saved successfully.');
                      } catch (requestError) {
                        if (requestError.message.toLowerCase().includes('token')) {
                          clearAdminSession();
                        }
                        setError(requestError.message);
                      }
                    }}
                  >
                    Save Settings
                  </button>
                </div>
              ) : null}

              {activePanel === 'theme' ? (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-white">UI Theme</h2>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {Object.keys(themeMap).map((themeKey) => (
                      <button
                        key={themeKey}
                        type="button"
                        onClick={() => setUiTheme(themeKey)}
                        className={`rounded-xl px-4 py-3 text-sm font-medium capitalize transition-all ${uiTheme === themeKey ? 'text-white' : 'text-slate-300 hover:text-white'}`}
                        style={uiTheme === themeKey ? { background: themeMap[themeKey].chip, border: themeMap[themeKey].border } : { border: '1px solid rgba(148, 163, 184, 0.22)' }}
                      >
                        {themeKey}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-slate-400">Selected theme: <span className="text-white capitalize">{uiTheme}</span></p>
                  <button
                    type="button"
                    className="neon-btn neon-btn-primary"
                    onClick={async () => {
                      setError('');
                      try {
                        await savePreferences({ theme: uiTheme });
                        setMessage('Theme saved successfully.');
                      } catch (requestError) {
                        if (requestError.message.toLowerCase().includes('token')) {
                          clearAdminSession();
                        }
                        setError(requestError.message);
                      }
                    }}
                  >
                    Save Theme
                  </button>
                </div>
              ) : null}

              {activePanel === 'invoice' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-xl font-semibold text-white">Invoice Manager</h2>
                    {invoiceView === 'list' ? (
                      <button
                        type="button"
                        className="neon-btn neon-btn-primary"
                        onClick={() => {
                          setInvoiceForm({ ...initialInvoiceForm, invoiceNumber: `INV-${Date.now().toString().slice(-6)}` });
                          setInvoiceView('create');
                        }}
                      >
                        <Plus className="w-4 h-4" />
                        New Invoice
                      </button>
                    ) : (
                      <button type="button" className="neon-btn neon-btn-outline" onClick={() => setInvoiceView('list')}>
                        <X className="w-4 h-4" />
                        Back to List
                      </button>
                    )}
                  </div>

                  {invoiceView === 'list' ? (
                    <div className="space-y-3">
                      {invoices.length === 0 ? (
                        <p className="text-slate-400 text-sm">No invoices yet. Create your first invoice.</p>
                      ) : (
                        invoices.map((inv) => {
                          const subtotal = inv.items.reduce((s, i) => s + i.quantity * i.rate, 0);
                          const total = subtotal + (subtotal * inv.tax) / 100;
                          return (
                            <div
                              key={inv.invoiceNumber}
                              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-accent/20 bg-slate-900/40 px-4 py-3"
                            >
                              <div>
                                <p className="text-white font-medium text-sm">{inv.invoiceNumber} — {inv.clientName}</p>
                                <p className="text-slate-400 text-xs">Due: {inv.dueDate} · ₹{total.toFixed(2)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-[11px] px-2 py-0.5 rounded-full border ${
                                    inv.status === 'paid'
                                      ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10'
                                      : 'text-amber-300 border-amber-500/30 bg-amber-500/10'
                                  }`}
                                >
                                  {inv.status === 'paid' ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <Clock className="w-3 h-3 inline mr-1" />}
                                  {inv.status}
                                </span>
                                <button
                                  type="button"
                                  className="neon-btn neon-btn-outline"
                                  onClick={() => { setPreviewInvoice(inv); setInvoiceView('preview'); }}
                                >
                                  <FileText className="w-4 h-4" />
                                  View
                                </button>
                                <button
                                  type="button"
                                  className="neon-btn neon-btn-primary"
                                  onClick={() =>
                                    setInvoices((prev) =>
                                      prev.map((i) =>
                                        i.invoiceNumber === inv.invoiceNumber
                                          ? { ...i, status: i.status === 'paid' ? 'unpaid' : 'paid' }
                                          : i
                                      )
                                    )
                                  }
                                >
                                  {inv.status === 'paid' ? 'Mark Unpaid' : 'Mark Paid'}
                                </button>
                                <button
                                  type="button"
                                  className="neon-btn neon-btn-outline"
                                  onClick={() =>
                                    setInvoices((prev) => prev.filter((i) => i.invoiceNumber !== inv.invoiceNumber))
                                  }
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ) : null}

                  {invoiceView === 'create' ? (
                    <form
                      className="space-y-4"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setInvoices((prev) => [{ ...invoiceForm, status: 'unpaid' }, ...prev]);
                        setInvoiceView('list');
                      }}
                    >
                      <div className="grid sm:grid-cols-2 gap-4">
                        <input
                          required
                          type="text"
                          placeholder="Client Name"
                          value={invoiceForm.clientName}
                          onChange={(e) => setInvoiceForm((p) => ({ ...p, clientName: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                        />
                        <input
                          type="email"
                          placeholder="Client Email"
                          value={invoiceForm.clientEmail}
                          onChange={(e) => setInvoiceForm((p) => ({ ...p, clientEmail: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                        />
                        <input
                          required
                          type="text"
                          placeholder="Invoice Number"
                          value={invoiceForm.invoiceNumber}
                          onChange={(e) => setInvoiceForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                        />
                        <input
                          required
                          type="date"
                          value={invoiceForm.dueDate}
                          onChange={(e) => setInvoiceForm((p) => ({ ...p, dueDate: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-slate-200"
                        />
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm text-slate-300 font-medium">Line Items</p>
                        {invoiceForm.items.map((item, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_80px_90px_36px] gap-2 items-center">
                            <input
                              required
                              type="text"
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) =>
                                setInvoiceForm((p) => {
                                  const items = [...p.items];
                                  items[idx] = { ...items[idx], description: e.target.value };
                                  return { ...p, items };
                                })
                              }
                              className="px-3 py-2 rounded-xl bg-surface/60 border border-accent/20 text-white text-sm"
                            />
                            <input
                              required
                              type="number"
                              min="1"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) =>
                                setInvoiceForm((p) => {
                                  const items = [...p.items];
                                  items[idx] = { ...items[idx], quantity: Number(e.target.value) };
                                  return { ...p, items };
                                })
                              }
                              className="px-3 py-2 rounded-xl bg-surface/60 border border-accent/20 text-white text-sm"
                            />
                            <input
                              required
                              type="number"
                              min="0"
                              placeholder="Rate ₹"
                              value={item.rate}
                              onChange={(e) =>
                                setInvoiceForm((p) => {
                                  const items = [...p.items];
                                  items[idx] = { ...items[idx], rate: Number(e.target.value) };
                                  return { ...p, items };
                                })
                              }
                              className="px-3 py-2 rounded-xl bg-surface/60 border border-accent/20 text-white text-sm"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setInvoiceForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }))
                              }
                              disabled={invoiceForm.items.length === 1}
                              className="text-rose-400 hover:text-rose-300 disabled:opacity-30"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="neon-btn neon-btn-outline text-xs"
                          onClick={() =>
                            setInvoiceForm((p) => ({
                              ...p,
                              items: [...p.items, { description: '', quantity: 1, rate: 0 }],
                            }))
                          }
                        >
                          <Plus className="w-3 h-3" />
                          Add Item
                        </button>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-slate-300 mb-1 block">Tax (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={invoiceForm.tax}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, tax: Number(e.target.value) }))}
                            className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-slate-300 mb-1 block">Notes</label>
                          <input
                            type="text"
                            placeholder="Payment terms, bank details..."
                            value={invoiceForm.notes}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, notes: e.target.value }))}
                            className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                          />
                        </div>
                      </div>

                      <div className="text-right text-sm text-slate-300">
                        {(() => {
                          const subtotal = invoiceForm.items.reduce((s, i) => s + i.quantity * i.rate, 0);
                          const tax = (subtotal * invoiceForm.tax) / 100;
                          return (
                            <>
                              <p>Subtotal: ₹{subtotal.toFixed(2)}</p>
                              <p>Tax ({invoiceForm.tax}%): ₹{tax.toFixed(2)}</p>
                              <p className="text-white font-semibold text-base">Total: ₹{(subtotal + tax).toFixed(2)}</p>
                            </>
                          );
                        })()}
                      </div>

                      <button type="submit" className="neon-btn neon-btn-primary w-full justify-center">
                        <FileText className="w-4 h-4" />
                        Save Invoice
                      </button>
                    </form>
                  ) : null}

                  {invoiceView === 'preview' && previewInvoice ? (() => {
                    const subtotal = previewInvoice.items.reduce((s, i) => s + i.quantity * i.rate, 0);
                    const tax = (subtotal * previewInvoice.tax) / 100;
                    const total = subtotal + tax;
                    return (
                      <div className="space-y-4">
                        <div
                          id="invoice-preview"
                          className="invoice-sheet print:rounded-none print:border-none print:shadow-none"
                        >
                          <div className="invoice-sheet__header">
                            <div className="invoice-sheet__brand-wrap">
                              <div className="invoice-sheet__logo-box">
                                <img src={companyLogo} alt="ClientCode AI" className="invoice-sheet__logo" />
                              </div>
                              <div>
                                <h3 className="invoice-sheet__brand">CLIENTCODE AI INNOVATION PRIVATE LIMITED</h3>
                                <p className="invoice-sheet__brand-sub">First Floor-102, SRS Corporate Tower, Sector 31, Faridabad, Haryana 121003</p>
                              </div>
                            </div>
                            <div className="invoice-sheet__meta">
                              <p className="invoice-sheet__title-tag">Tax Invoice</p>
                              <p><span>Invoice No.</span> <strong>{previewInvoice.invoiceNumber}</strong></p>
                              <p><span>Issue Date</span> <strong>{previewInvoice.issueDate}</strong></p>
                              <p><span>Due Date</span> <strong>{previewInvoice.dueDate || '-'}</strong></p>
                            </div>
                          </div>

                          <div className="invoice-sheet__body">
                            <div className="invoice-sheet__party-grid">
                              <div className="invoice-sheet__party-card">
                                <p className="invoice-sheet__section-label">From - Seller Details</p>
                                <p className="invoice-sheet__party-name">CLIENTCODE AI INNOVATION PRIVATE LIMITED</p>
                                <p className="invoice-sheet__party-sub">support@clientcodeai.in</p>
                                <p className="invoice-sheet__party-sub">+91 95822 92739</p>
                              </div>

                              <div className="invoice-sheet__party-card">
                                <p className="invoice-sheet__section-label">Bill To - Client Details</p>
                                <p className="invoice-sheet__party-name">{previewInvoice.clientName || '-'}</p>
                                <p className="invoice-sheet__party-sub">{previewInvoice.clientEmail || '-'}</p>
                              </div>
                            </div>

                            <table className="invoice-sheet__table">
                              <thead>
                                <tr>
                                  <th className="text-left">Description</th>
                                  <th className="text-right">Qty</th>
                                  <th className="text-right">Rate</th>
                                  <th className="text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {previewInvoice.items.map((item, i) => (
                                  <tr key={i}>
                                    <td>{item.description || '-'}</td>
                                    <td className="text-right">{item.quantity}</td>
                                    <td className="text-right">₹{item.rate.toFixed(2)}</td>
                                    <td className="text-right">₹{(item.quantity * item.rate).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            <div className="invoice-sheet__totals-wrap">
                              <div className="invoice-sheet__totals-card">
                                <div className="invoice-sheet__total-line">
                                  <span>Subtotal</span>
                                  <strong>₹{subtotal.toFixed(2)}</strong>
                                </div>
                                <div className="invoice-sheet__total-line">
                                  <span>Tax ({previewInvoice.tax}%)</span>
                                  <strong>₹{tax.toFixed(2)}</strong>
                                </div>
                                <div className="invoice-sheet__total-line invoice-sheet__total-line--grand">
                                  <span>Total</span>
                                  <strong>₹{total.toFixed(2)}</strong>
                                </div>
                              </div>
                            </div>

                            {previewInvoice.notes ? (
                              <p className="invoice-sheet__notes">{previewInvoice.notes}</p>
                            ) : null}
                          </div>

                          <div className="invoice-sheet__footer">This is a computer-generated invoice. No signature required.</div>
                        </div>
                        <button
                          type="button"
                          className="neon-btn neon-btn-primary no-print"
                          onClick={() => window.print()}
                        >
                          <Download className="w-4 h-4" />
                          Download / Print PDF
                        </button>
                      </div>
                    );
                  })() : null}
                </div>
              ) : null}

              {activePanel === 'salary-slip' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-xl font-semibold text-white">Salary Slip Generator</h2>
                    <a
                      href="/salary-slip-generator.html"
                      target="_blank"
                      rel="noreferrer"
                      className="neon-btn neon-btn-outline"
                    >
                      Open In New Tab
                    </a>
                  </div>
                  <div className="rounded-xl overflow-hidden border border-accent/20 bg-white h-[78vh] min-h-[640px]">
                    <iframe
                      title="Salary Slip Generator"
                      src="/salary-slip-generator.html"
                      className="w-full h-full"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </motion.div>
      </section>
      <FooterNew />
    </main>
  );
};

export default AdminSocialPost;